import React from "react";
import { Target, Scale, Percent } from "lucide-react";

// Format a R:R ratio like "1 : 1.75". If the trader hasn't taken a losing trade
// yet, avg_loss is 0 → ratio undefined; show "∞" so the number is still readable.
function formatRR(avgWin, avgLoss) {
  if (avgLoss <= 0 && avgWin <= 0) return "—";
  if (avgLoss <= 0) return "∞";
  const ratio = avgWin / avgLoss;
  return `1 : ${ratio.toFixed(2)}`;
}

function InsightCard({ label, value, sub, tone, icon: Icon }) {
  const toneCls =
    tone === "emerald" ? "text-emerald-700" :
    tone === "rose" ? "text-rose-700" :
    tone === "brand" ? "text-[#00b4ff]" : "text-slate-900";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono-plex font-semibold tracking-widest uppercase text-slate-500">{label}</div>
        {Icon && <Icon size={14} className="text-slate-300" />}
      </div>
      <div className={`mt-2 font-display font-bold text-2xl md:text-3xl tracking-tight ${toneCls}`}>{value}</div>
      {sub && <div className="mt-1 text-[10px] font-mono-plex tracking-widest text-slate-500">{sub}</div>}
    </div>
  );
}

/**
 * Trader progress insights — win rate + average risk:reward — rendered as a
 * compact 3-card row above the P&L chart.
 *
 * Takes the same `totals` object produced by <TradeJournal>.
 */
export default function JournalInsights({ totals }) {
  const { wins, losses, profit, loss } = totals;
  const tradesCount = wins + losses;
  const winRate = tradesCount > 0 ? (wins / tradesCount) * 100 : 0;
  const avgWin = wins > 0 ? profit / wins : 0;
  const avgLoss = losses > 0 ? loss / losses : 0;

  let winRateTone = "slate";
  if (tradesCount > 0) winRateTone = winRate >= 50 ? "emerald" : "rose";

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4" data-testid="journal-insights">
      <InsightCard
        label="Win Rate"
        value={tradesCount > 0 ? `${winRate.toFixed(1)}%` : "—"}
        sub={tradesCount > 0 ? `${wins}W · ${losses}L` : "No trades yet"}
        tone={winRateTone}
        icon={Percent}
      />
      <InsightCard
        label="Avg R:R"
        value={formatRR(avgWin, avgLoss)}
        sub={
          tradesCount > 0
            ? `avg win $${avgWin.toFixed(2)} · avg loss $${avgLoss.toFixed(2)}`
            : "Log trades to see ratio"
        }
        tone="brand"
        icon={Scale}
      />
      <InsightCard
        label="Trades Taken"
        value={tradesCount}
        sub={tradesCount > 0 ? `Total P&L ${totals.netPnl >= 0 ? "+" : "-"}$${Math.abs(totals.netPnl).toFixed(2)}` : "This month"}
        tone="slate"
        icon={Target}
      />
    </div>
  );
}
